/* eslint-disable react-hooks/set-state-in-effect -- legitimate setState in mount-time
   content hydration + admin-initiated update/reset event handlers. */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { DEFAULT_CONTENT } from "@/lib/defaults";

const ContentContext = createContext(null);

const CACHE_KEY = "pf_content_cache_v1";
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h

const loadCached = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.data || !parsed.ts) return null;
    if (Date.now() - parsed.ts > CACHE_MAX_AGE_MS) return null;
    return parsed.data;
  } catch (_e) {
    return null;
  }
};

const saveCached = (data) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ data, ts: Date.now() })
    );
  } catch (_e) {
    // quota or disabled storage — ignore
  }
};

const arr = (value, fallback) => (Array.isArray(value) ? value : fallback);

const mergeWithDefaults = (data) => {
  if (!data) return DEFAULT_CONTENT;
  return {
    id: data.id || "site",
    hero: {
      ...DEFAULT_CONTENT.hero,
      ...(data.hero || {}),
      floating_banners: arr(data.hero?.floating_banners, DEFAULT_CONTENT.hero.floating_banners),
    },
    featured: {
      ...DEFAULT_CONTENT.featured,
      ...(data.featured || {}),
      pillars: (data.featured?.pillars?.length ? data.featured.pillars : DEFAULT_CONTENT.featured.pillars),
      checks: (data.featured?.checks?.length ? data.featured.checks : DEFAULT_CONTENT.featured.checks),
      floating_banners: arr(data.featured?.floating_banners, DEFAULT_CONTENT.featured.floating_banners),
    },
    portfolio: {
      ...DEFAULT_CONTENT.portfolio,
      ...(data.portfolio || {}),
      cards: (data.portfolio?.cards?.length ? data.portfolio.cards : DEFAULT_CONTENT.portfolio.cards),
    },
    about: {
      ...DEFAULT_CONTENT.about,
      ...(data.about || {}),
      values: (data.about?.values?.length ? data.about.values : DEFAULT_CONTENT.about.values),
    },
    footer: {
      ...DEFAULT_CONTENT.footer,
      ...(data.footer || {}),
      socials: (data.footer?.socials?.length ? data.footer.socials : DEFAULT_CONTENT.footer.socials),
    },
  };
};

export const ContentProvider = ({ children }) => {
  const cached = loadCached();
  const [content, setContent] = useState(cached ? mergeWithDefaults(cached) : DEFAULT_CONTENT);
  // "loading" means "the page has never received a fresh API response in this session".
  // If we have valid cached content, we still mark loading=true so the UI knows it might be stale,
  // but consumers can rely on `hasCached` to skip skeletons when they have plausibly correct data.
  const [loading, setLoading] = useState(true);
  const [hasCached] = useState(Boolean(cached));

  // Initial content fetch + cache update.
  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/content");
      const merged = mergeWithDefaults(res.data);
      setContent(merged);
      saveCached(res.data);
    } catch (_e) {
      // keep current (defaults or cached) content
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh runs once on mount.
  useEffect(() => {
    refresh();
  }, [refresh]);

  // Event handler: admin-initiated update; setState updates UI after save.
  const update = useCallback(async (section, data) => {
    const res = await api.put("/content", { [section]: data });
    setContent(mergeWithDefaults(res.data));
    saveCached(res.data);
    return res.data;
  }, []);

  // Event handler: admin-initiated reset.
  const reset = useCallback(async () => {
    const res = await api.post("/content/reset");
    setContent(mergeWithDefaults(res.data));
    saveCached(res.data);
    return res.data;
  }, []);

  return (
    <ContentContext.Provider value={{ content, loading, hasCached, refresh, update, reset }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const ctx = useContext(ContentContext);
  if (!ctx) throw new Error("useContent must be used within ContentProvider");
  return ctx;
};
